import apiService from './api';
import type { ApiResponse } from '../types';

/**
 * Xero Payroll (AU), over the organisation's own Xero.
 *
 * The connection itself (sign-in, choice of organisation, disconnect) is not
 * here: payroll and invoicing share one, in `xeroConnection.ts`. This is what
 * payroll does with it.
 *
 * Namespaced under `/xero-payroll` rather than `/xero`: the latter is the
 * system-wide billing integration (`subscription.ts` +
 * `pages/subscriptions/XeroAdmin.tsx`), FactreeTech's own Xero.
 *
 * Backend: HHNodeServer `routes/xeroPayroll.js` + `services/xeroPayrollService.js`.
 *
 * Scope limits worth knowing before building on this (Xero Payroll AU v1.0):
 *  - Timesheet periods must align exactly to a payroll calendar period, which
 *    is why `listPayrollCalendars` exists — ranges are derived, not picked.
 *  - A timesheet line carries a single tracking item, so farm/block/area
 *    costing stays in FactreeTech and is flattened on the way out.
 *  - STP lodgement and Auto Super batch authorisation are not exposed on the
 *    API. They remain manual steps in Xero after a pay run is posted.
 *
 * Payroll by API (see `payrollPosting.ts`) posts timesheets and leave through
 * this same connection, and — once HR switches `leave_source` to 'xero' —
 * leave balances are read back from Xero instead of calculated here.
 */

export interface XeroPayrollStatus {
  /** Connected for payroll: the Xero sign-in exists and holds payroll's permissions. */
  connected: boolean;
  /** The Xero sign-in exists, whatever it can reach. */
  xero_connected?: boolean;
  tenant_selected: boolean;
  xero_user_name: string | null;
  tenant_id?: string | null;
  tenant_name: string | null;
  connected_at: string | null;
  token_expires_at?: string | null;
  last_export_at: string | null;
  last_export_status: 'success' | 'partial' | 'failed' | null;
  last_export_error: string | null;
  /** Where leave balances come from for this org. */
  leave_source?: 'local' | 'xero';
  leave_source_since?: string | null;
  last_balance_sync_at?: string | null;
  last_balance_sync_error?: string | null;
  /** False when the grant predates payroll posting: reconnect to post. */
  can_post?: boolean;
  missing_scopes?: string[];
}

/** A Xero leave type (PayItems.LeaveTypes). */
export interface XeroLeaveType {
  leave_type_id: string;
  name: string;
  type_of_units: string | null;
  is_paid_leave: boolean;
}

/** FactreeTech leave types that can be owned by Xero. */
export type MappableLeaveType = 'annual' | 'personal' | 'long_service' | 'other';

export interface XeroLeaveTypeMapping {
  leave_type: MappableLeaveType;
  xero_leave_type_id: string;
  xero_leave_type_name: string | null;
}

export interface XeroLeaveReadiness {
  leave_source: 'local' | 'xero';
  leave_source_since: string | null;
  last_balance_sync_at: string | null;
  last_balance_sync_error: string | null;
  can_post: boolean;
  missing_scopes: string[];
  unmapped_leave_types: MappableLeaveType[];
  unlinked_users: { user_id: number; name: string }[];
  ready: boolean;
}

export interface XeroBalanceSyncResult {
  synced: number;
  skipped: number;
  failed: { user_id: number; error: string }[];
}

export interface XeroPayrollCalendar {
  payroll_calendar_id: string;
  name: string;
  /** WEEKLY | FORTNIGHTLY | FOURWEEKLY | MONTHLY | TWICEMONTHLY | QUARTERLY */
  calendar_type: string;
  /** YYYY-MM-DD — period start, used to derive valid timesheet ranges. */
  start_date: string;
  payment_date: string;
}

/** A Xero earnings rate, mapped against an internal payroll category. */
export interface XeroEarningsRate {
  earnings_rate_id: string;
  name: string;
  /** ORDINARYTIMEEARNINGS | OVERTIMEEARNINGS | ALLOWANCE | ... */
  earnings_type?: string;
  /** RATEPERUNIT | MULTIPLE | FIXEDAMOUNT */
  rate_type?: string;
  /** Typically 'Hours'. Non-hour units can't be fed from timesheets. */
  type_of_units?: string;
  is_exempt_from_tax?: boolean;
  is_exempt_from_super?: boolean;
}

export interface XeroEmployee {
  xero_employee_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  /** ACTIVE | TERMINATED */
  status: string | null;
  termination_date: string | null;
}

/** One FactreeTech user, their current link, and the server's suggestion. */
export interface XeroEmployeeMatchUser {
  user_id: number;
  username: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  xero_employee_id: string | null;
  xero_employee_name: string | null;
  /** Linked to an employee Xero no longer returns. */
  link_stale: boolean;
  suggested_xero_employee_id: string | null;
  suggestion_reason: 'email' | 'name' | null;
}

export interface XeroEmployeeMatching {
  tenant_name: string | null;
  employees: XeroEmployee[];
  users: XeroEmployeeMatchUser[];
}

class XeroPayrollService {
  async getStatus(): Promise<ApiResponse<XeroPayrollStatus>> {
    return apiService.get('/xero-payroll/status');
  }

  /** Drives the pay-period picker; ranges must line up with a calendar. */
  async listPayrollCalendars(): Promise<ApiResponse<XeroPayrollCalendar[]>> {
    return apiService.get('/xero-payroll/payroll-calendars');
  }

  /** Feeds the mapping screen (`PayrollExportMappings`, target='xero'). */
  async listEarningsRates(): Promise<ApiResponse<XeroEarningsRate[]>> {
    return apiService.get('/xero-payroll/earnings-rates');
  }

  async getEmployeeMatching(): Promise<ApiResponse<XeroEmployeeMatching>> {
    return apiService.get('/xero-payroll/employee-matching');
  }

  async listLeaveTypes(): Promise<ApiResponse<XeroLeaveType[]>> {
    return apiService.get('/xero-payroll/leave-types');
  }

  async getLeaveTypeMap(): Promise<ApiResponse<XeroLeaveTypeMapping[]>> {
    return apiService.get('/xero-payroll/leave-type-map');
  }

  /** `xero_leave_type_id: null` clears that type. */
  async saveLeaveTypeMap(
    map: { leave_type: MappableLeaveType; xero_leave_type_id: string | null }[]
  ): Promise<ApiResponse<XeroLeaveTypeMapping[]>> {
    return apiService.put('/xero-payroll/leave-type-map', { map });
  }

  async getLeaveReadiness(): Promise<ApiResponse<XeroLeaveReadiness>> {
    return apiService.get('/xero-payroll/leave-readiness');
  }

  /** Switching to 'xero' also pulls balances straight away. */
  async setLeaveSource(
    source: 'local' | 'xero'
  ): Promise<ApiResponse<XeroLeaveReadiness & { sync: XeroBalanceSyncResult | null }>> {
    return apiService.put('/xero-payroll/leave-source', { source }, undefined, { timeout: 180_000 });
  }

  async syncLeaveBalances(userId?: number): Promise<ApiResponse<XeroBalanceSyncResult>> {
    return apiService.post('/xero-payroll/leave-balances/sync', userId ? { user_id: userId } : {}, undefined, { timeout: 180_000 });
  }

  /** `xero_employee_id: null` unlinks. Users not listed are left alone. */
  async saveEmployeeLinks(
    links: { user_id: number; xero_employee_id: string | null }[]
  ): Promise<ApiResponse<XeroEmployeeMatching>> {
    return apiService.put('/xero-payroll/employee-links', { links });
  }
}

export const xeroPayrollService = new XeroPayrollService();
export default xeroPayrollService;
